import { Routes } from '@angular/router';
import { EnhancedAuthGuard } from 'src/app/core/guards/enhanced-auth.guard';
import { ProfileResolverService } from 'src/app/core/guards/profile-resolver.service';
import { RouteData } from 'src/app/core/models/route-data';

/**
 * Producer profile and carrier/agency configuration routes. Grouped here because profile
 * and configuration each use a custom named outlet (profile-content, configuration-content)
 * for their tab children, owned by their respective parent components.
 */
export const PROFILE_CONFIG_ROUTES: Routes = [
    {
        path: 'profile',
        loadComponent: () => import('src/components/profile/profile.component').then(m => m.ProfileComponent),
        canActivate: [EnhancedAuthGuard],
        resolve: {
            profileData: ProfileResolverService
        },
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                redirectTo: 'overview',
                pathMatch: 'full'
            },
            {
                path: 'overview',
                loadComponent: () => import('src/components/profile/overview/profile-overview.component').then(m => m.ProfileOverviewComponent)
            },
            {
                path: 'licenses',
                loadComponent: () => import('src/components/licenses/licenses-v2.component').then(m => m.LicensesV2Component),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'profileLicenses',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'errorAndOmissionPolicies',
                loadComponent: () => import('src/components/personal-summary/error-and-omissions/error-and-omissions.component').then(m => m.ErrorAndOmissionsComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'profileErrorAndOmissionPolicies',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'carrierBranding',
                loadComponent: () => import('src/components/carrier-branding/carrier-branding.component').then(m => m.CarrierBrandingComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'profileCarrierBranding',
                    requiresPermission: true
                } satisfies RouteData
            }
        ]
    },

    {
        path: 'configuration',
        loadComponent: () => import('src/components/configuration-console/configuration-console.component').then(m => m.ConfigurationConsoleComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'configuration',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'configureAccess'
            },
            {
                path: 'configureAccess',
                loadComponent: () => import('src/components/configuration-console/configure-access/configure-access.component').then(m => m.ConfigureAccessComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'configurationConfigureAccess',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'users',
                loadComponent: () => import('src/components/users/users.component').then(m => m.UsersComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'configurationUsers',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'manageCustomQuestionnaires',
                loadComponent: () => import('src/components/manage-custom-questionnaires/manage-custom-questionnaires.component').then(m => m.ManageCustomQuestionnairesComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    requiresPermission: false
                } satisfies RouteData
            },
            {
                path: 'manageDocuments',
                loadComponent: () => import('src/components/manage-documents/manage-documents.component').then(m => m.ManageDocumentsComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'configurationManageDocuments',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'producerRules',
                loadComponent: () => import('src/components/manage-rules/manage-rules.component').then(m => m.ManageRulesComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'configurationManageRules',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'compliance',
                loadComponent: () => import('src/components/configuration-console/compliance/compliance-config.component').then(m => m.ComplianceConfigComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'configurationCompliance',
                    requiresPermission: true
                } satisfies RouteData,
                children: [
                    {
                        path: '',
                        redirectTo: 'errors-and-omissions',
                        pathMatch: 'full'
                    },
                    {
                        path: 'errors-and-omissions',
                        loadComponent: () => import('src/components/configuration-console/compliance/errors-and-omissions/errors-and-omissions-config.component').then(m => m.ErrorsAndOmissionsConfigComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'configurationComplianceErrorsAndOmissions',
                            requiresPermission: true
                        } satisfies RouteData
                    }
                ]
            },
            {
                path: 'configureRelationships',
                loadComponent: () => import('src/components/configuration-console/configure-relationships/configure-relationships.component').then(m => m.ConfigureRelationshipsComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'configurationRelationships',
                    requiresPermission: true
                } satisfies RouteData,
                children: [
                    // NOTE: ConfigureRelationshipsComponent renders its subtabs INLINE (see its template -
                    // no <router-outlet>), and its ngOnInit builds the tab list from the user's permissions
                    // and defaults to the first PERMITTED tab. The previous empty-path `redirectTo: 'contracts'`
                    // forced every landing onto the contracts child route, whose guard then bounced any user
                    // without contracts permission to /error - even users who legitimately had another
                    // relationship tab (e.g. termination reasons). Removing the redirect lets the component
                    // land on the first tab the user can actually see. The child routes below are retained
                    // only for URL sync when tabChange() navigates. (Router-driven tabs are FOF-7762.)
                    {
                        path: 'contracts',
                        loadComponent: () => import('src/components/configuration-console/configure-contracts/configure-contracts.component').then(m => m.ConfigureContractsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'configurationRelationshipsContracts',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'compSchedules',
                        loadComponent: () => import('src/components/configuration-console/configure-comp-schedules/configure-comp-schedules.component').then(m => m.ConfigureCompSchedulesComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'configurationRelationshipsCompSchedules',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'terminationReasons',
                        loadComponent: () => import('src/components/configuration-console/configure-relationships/termination-reasons/termination-reasons.component').then(m => m.TerminationReasonsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'configurationRelationshipsTerminationReasons',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'paymentPreferences',
                        loadComponent: () => import('src/components/configuration-console/configure-payment-preferences/configure-payment-preferences.component').then(m => m.ConfigurePaymentPreferencesComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            requiresPermission: false
                        } satisfies RouteData
                    },
                    {
                        path: 'scheduleGroups',
                        loadComponent: () => import('src/components/configuration-console/configure-schedule-groups/configure-schedule-groups.component').then(m => m.ConfigureScheduleGroupsComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            requiresPermission: true,
                            permissionKey: 'configurationRelationshipsScheduleGroups',
                        } satisfies RouteData
                    }
                ]
            }
        ]
    },

    {
        path: 'terminationReasonsWorkflow/:type',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'terminationReasonsWorkflow',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/configuration-console/configure-relationships/termination-reasons/termination-reasons-workflow/termination-reasons-workflow.component').then(m => m.TerminationReasonsWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/configuration-console/configure-relationships/termination-reasons/termination-reasons-workflow/termination-reasons-workflow-side-rail/termination-reasons-workflow-side-rail.component').then(m => m.TerminationReasonsWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'paymentPreferencesWorkflow/:type',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false
        } satisfies RouteData,
        children: [
            {
                path: '',
                // FOF-7882: global masthead renders at app level; empty outlet avoids a blank duplicate.
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/configuration-console/configure-payment-preferences/payment-preferences-workflow/payment-preferences-workflow.component').then(m => m.PaymentPreferencesWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/configuration-console/configure-payment-preferences/payment-preferences-workflow/payment-preferences-workflow-side-rail/payment-preferences-workflow-side-rail.component').then(m => m.PaymentPreferencesWorkflowSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    },

    {
        path: 'errorsAndOmissionsWorkflow/:type',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'configurationComplianceErrorsAndOmissions',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'header'
            },
            {
                path: '',
                loadComponent: () => import('src/components/configuration-console/compliance/errors-and-omissions/errors-and-omissions-workflow/errors-and-omissions-workflow.component').then(m => m.ErrorsAndOmissionsWorkflowComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/configuration-console/compliance/errors-and-omissions/errors-and-omissions-workflow/errors-and-omissions-workflow-side-rail/errors-and-omissions-workflow-side-rail.component').then(m => m.ErrorsAndOmissionsWorkflowSideRailComponent),
                outlet: 'sideRail'
            },
            {
                path: '',
                loadComponent: () => import('src/app/empty.component').then(m => m.EmptyComponent),
                outlet: 'footer'
            }
        ]
    },

    {
        path: 'configureCarrierFeatureChanges',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'configureCarrierFeatureChanges',
            requiresPermission: true,
            hideDefaultSideRail: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/configure-carrier-feature-changes/configure-carrier-feature-changes.component').then(m => m.ConfigureCarrierFeatureChangesComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/insurance/insurance-workflow-side-rail.component').then(m => m.InsuranceWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'configureAccessPermissions',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),  // Changed from EmptyComponent - Issue #32 fix
        canActivate: [EnhancedAuthGuard],
        data: {
            permissionKey: 'configureAccessPermissions',
            requiresPermission: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/configure-access-permissions/configure-access-permissions.component').then(m => m.ConfigureAccessPermissionsComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/workflow/system-capability/system-capability-workflow-side-rail.component').then(m => m.SystemCapabilityWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    },

    {
        path: 'configureProfile',
        loadComponent: () => import('src/app/multi-outlet-container.component').then(m => m.MultiOutletContainerComponent),
        canActivate: [EnhancedAuthGuard],
        data: {
            requiresPermission: false,
            hideDefaultSideRail: true
        } satisfies RouteData,
        children: [
            {
                path: '',
                loadComponent: () => import('src/components/compliance-dashboard/configure-compliance-profile/configure-compliance-profile.component').then(m => m.ConfigureComplianceProfileComponent),
                outlet: 'content'
            },
            {
                path: '',
                loadComponent: () => import('src/components/compliance-dashboard/configure-compliance-profile-workflow-side-rail/configure-compliance-profile-workflow-side-rail.component').then(m => m.ConfigureComplianceProfileWorkflowSideRailComponent),
                outlet: 'sideRail'
            }
        ]
    }
];
